import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { EnvelopeIcon, PhoneIcon, ChatBubbleLeftRightIcon, ChevronDownIcon, CheckIcon } from '@heroicons/react/20/solid'
import LeadsService from '@/services/LeadsService'
import Swal from 'sweetalert2'

const initialsFromName = (name = '') =>
	name
		.split(' ')
		.filter(Boolean)
		.map(n => n[0]?.toUpperCase())
		.slice(0, 2)
		.join('') || '—'

const STATUS_OPTIONS = [
	{ value: 'new', label: 'New', color: 'bg-blue-500', textColor: 'text-white' },
	{ value: 'won', label: 'Won', color: 'bg-green-500', textColor: 'text-white' },
	{ value: 'lost', label: 'Lost', color: 'bg-red-500', textColor: 'text-white' },
]

export default function LeadInfoCard({ lead, onEdit, onDelete, deleting = false, onStatusUpdate }) {
	const navigate = useNavigate()
	const tel = lead?.customerTel?.trim() || ''
	const email = lead?.customerEmail?.trim() || ''
	const source = lead?.source || '—'
	const status = lead?.status || 'new'
	const leadId = lead?._id

	const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false)
	const [updatingStatus, setUpdatingStatus] = useState(false)
	const dropdownRef = useRef(null)

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = event => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsStatusDropdownOpen(false)
			}
		}

		if (isStatusDropdownOpen) {
			document.addEventListener('mousedown', handleClickOutside)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [isStatusDropdownOpen])

	const handleConvert = () => {
		if (!leadId) return
		navigate(`/system/customers/add?leadId=${encodeURIComponent(leadId)}`)
	}

	const handleStatusChange = async newStatus => {
		if (!leadId || newStatus === status || updatingStatus) return

		setUpdatingStatus(true)
		setIsStatusDropdownOpen(false)

		try {
			const { success, error } = await LeadsService.updateLeadStatus(leadId, newStatus)
			if (success) {
				// Refresh the lead data
				if (onStatusUpdate) {
					await onStatusUpdate()
				}
			} else {
				await Swal.fire({
					title: 'Error',
					text: error || 'Failed to update lead status',
					icon: 'error',
				})
			}
		} catch (err) {
			console.error('Error updating lead status:', err)
			await Swal.fire({
				title: 'Error',
				text: 'An unexpected error occurred while updating the status',
				icon: 'error',
			})
		} finally {
			setUpdatingStatus(false)
		}
	}

	const currentStatusOption = STATUS_OPTIONS.find(opt => opt.value === status) || STATUS_OPTIONS[0]
	let statusColor = currentStatusOption.color
	let statusTextColor = currentStatusOption.textColor

	return (
		<div className="flex h-full max-h-[600px] w-full flex-col rounded-xl bg-white shadow-sm">
			<div className="flex-1 space-y-6 overflow-y-auto p-6">
				{/* Avatar + name - centered */}
				<div className="flex flex-col items-center">
					<div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-600">
						<span className="text-xl font-semibold text-white">{initialsFromName(lead?.customerName)}</span>
					</div>
					<h3 className="mt-4 text-xl font-bold text-gray-900">{lead?.customerName || '—'}</h3>
				</div>

				{/* Quick contact icons - small circular icons */}
				<div className="flex items-center justify-center gap-3">
					{tel ? (
						<a
							href={`tel:${tel}`}
							className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
							<PhoneIcon className="h-5 w-5 text-gray-600" />
						</a>
					) : (
						<div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-400 cursor-not-allowed">
							<PhoneIcon className="h-5 w-5" />
						</div>
					)}

					{tel ? (
						<a
							href={`https://wa.me/${tel.replace(/\D/g, '')}`}
							target="_blank"
							rel="noopener noreferrer"
							className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
							<ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-600" />
						</a>
					) : (
						<div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-400 cursor-not-allowed">
							<ChatBubbleLeftRightIcon className="h-5 w-5" />
						</div>
					)}

					{email ? (
						<a
							href={`mailto:${email}`}
							className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
							<EnvelopeIcon className="h-5 w-5 text-gray-600" />
						</a>
					) : (
						<div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-400 cursor-not-allowed">
							<EnvelopeIcon className="h-5 w-5" />
						</div>
					)}
				</div>

				{/* Details - two column layout */}
				<div className="space-y-4">
					<div className="flex items-center justify-between text-sm">
						<span className="text-gray-500">Phone</span>
						<span className="font-medium text-gray-900">{tel || '—'}</span>
					</div>
					<div className="flex items-center justify-between text-sm">
						<span className="text-gray-500">Email</span>
						<span className="font-medium text-gray-900 break-all">{email || '—'}</span>
					</div>
					<div className="flex items-center justify-between text-sm">
						<span className="text-gray-500">Source</span>
						<span className="inline-flex items-center rounded-full bg-blue-500 px-2.5 py-0.5 text-xs font-medium text-white">
							{source}
						</span>
					</div>
					<div className="flex items-center justify-between text-sm">
						<span className="text-gray-500">Status</span>
						<div className="relative" ref={dropdownRef}>
							<button
								type="button"
								onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
								disabled={updatingStatus || !leadId}
								className={`inline-flex items-center gap-1.5 rounded-full ${statusColor} ${statusTextColor} px-2.5 py-0.5 text-xs font-medium transition-all hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed`}
							>
								{updatingStatus ? (
									<>
										<svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
											<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
											<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
										</svg>
										Updating...
									</>
								) : (
									<>
										{currentStatusOption.label}
										<ChevronDownIcon className="h-3 w-3" />
									</>
								)}
							</button>

							{isStatusDropdownOpen && !updatingStatus && (
								<div className="absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
									<div className="py-1" role="menu">
										{STATUS_OPTIONS.map(option => (
											<button
												key={option.value}
												type="button"
												onClick={() => handleStatusChange(option.value)}
												className={`w-full flex items-center justify-between px-4 py-2 text-sm transition-colors ${
													status === option.value
														? `${option.color} ${option.textColor} font-medium`
														: 'text-gray-700 hover:bg-gray-100'
												}`}
												role="menuitem"
											>
												<span>{option.label}</span>
												{status === option.value && <CheckIcon className="h-4 w-4" />}
											</button>
										))}
									</div>
								</div>
							)}
						</div>
					</div>
				</div>

				{/* Primary actions */}
				<div className="space-y-3 pt-2">
					<button
						type="button"
						onClick={handleConvert}
						disabled={!leadId}
						className={`w-full rounded-lg px-4 py-3 text-sm font-medium transition-colors ${
							leadId
								? 'bg-purple-700 text-white hover:bg-purple-800'
								: 'cursor-not-allowed bg-gray-200 text-gray-500'
						}`}
					>
						Convert to Customer
					</button>

					<button
						type="button"
						onClick={onEdit}
						className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-900 hover:bg-gray-50 transition-colors">
						Edit Lead
					</button>

					<button
						type="button"
						onClick={onDelete}
						disabled={deleting}
						className={`w-full text-sm font-medium transition-colors ${
							deleting ? 'cursor-not-allowed text-gray-400' : 'text-red-600 hover:text-red-700'
						}`}>
						{deleting ? 'Deleting…' : 'Delete Lead'}
					</button>
				</div>
			</div>
		</div>
	)
}
